import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, FlaskConical, Activity, Target, Zap, Brain, Flame } from "lucide-react";
import DiscountCTA from "@/components/DiscountCTA";
import FeaturedSourceCard from "@/components/FeaturedSourceCard";
import SEOHead from "@/components/SEOHead";

const PuritySection = lazy(() => import("@/components/PuritySection"));
const WhyGLP3R = lazy(() => import("@/components/WhyGLP3R"));

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/a2b41fc2-bfd1-45de-9c37-8a2cd443caf0/images/849054f8fbce9ec0c8b8cea3b4d8028ac21416ed151e18f51c10114cd87c5bd0.png";

export default function HomePage() {
  return (
    <article data-testid="home-page" id="master-guide">
      <SEOHead
        title="Retatrutide Master Guide — GLP-1/GIP/GCGR Triple-Agonist"
        description="The definitive scientific guide to Retatrutide (LY3437943). Phase III TRIUMPH-4: 28.7% weight loss at 68 weeks. Clinical data, dosage protocols, and where to buy research-grade GLP3-R."
        path="/"
        type="article"
      />
      {/* Hero */}
      <section data-testid="hero-section" className="py-16 md:py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 stagger-children">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-slate-200 rounded-full shadow-card mb-5">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs font-semibold text-slate-500 tracking-wide">
                  Comprehensive Research Monograph
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] mb-3">
                <span className="text-gradient">Retatrutide: The Triple-Agonist</span>{" "}
                <span className="text-slate-400">Redefining Metabolic Research</span>
              </h1>
              <h2
                data-testid="hero-where-to-get"
                className="text-xl sm:text-2xl font-extrabold tracking-tight mb-6"
                style={{ color: "#113C6E" }}
              >
                Where to Get Retatrutide?
              </h2>
              <p className="text-lg leading-relaxed text-slate-500 mb-8 max-w-xl">
                A complete scientific guide to LY3437943 — the first-in-class GLP-1/GIP/GCGR
                tri-agonist. Phase III TRIUMPH-4 data shows up to <span className="font-bold text-slate-700">28.7%</span> body
                weight reduction at 68 weeks. Get the gold-standard research compound below.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://bioinfinity.co/retatrutide/glp3-r"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-buy-btn"
                  className="btn-lift inline-flex items-center gap-2 px-7 py-3.5 text-white text-sm font-bold rounded-2xl transition-colors"
                  style={{ background: "#113C6E" }}
                >
                  Buy GLP3-R Now
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link
                  to="/clinical-data"
                  data-testid="hero-cta-clinical"
                  className="btn-lift inline-flex items-center gap-2 px-6 py-3.5 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-2xl shadow-card hover:border-slate-300 transition-all"
                >
                  View Clinical Data
                </Link>
                <Link
                  to="/dosage-calc"
                  data-testid="hero-cta-dosage"
                  className="btn-lift inline-flex items-center gap-2 px-6 py-3.5 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-2xl shadow-card hover:border-slate-300 transition-all"
                >
                  Dosage Calculator
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="bento-card p-4 overflow-hidden">
                <img
                  src={HERO_IMG}
                  alt="3D rendering of Retatrutide triple-agonist peptide molecular structure"
                  className="w-full rounded-xl"
                  loading="eager"
                  width="600"
                  height="600"
                  fetchPriority="high"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Source Card — ABOVE THE FOLD */}
      <section data-testid="featured-source-section" className="pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FeaturedSourceCard />
        </div>
      </section>

      {/* Triple Mechanism Bento */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Brain, title: "GLP-1 Agonism", sub: "Appetite & Glycemia", color: "from-blue-500 to-blue-700", desc: "Central satiety signaling via hypothalamic POMC/AgRP neurons, glucose-dependent insulin secretion, and delayed gastric emptying." },
              { icon: Zap, title: "GIP Agonism", sub: "Incretin Potentiation", color: "from-violet-500 to-violet-700", desc: "Synergistic beta-cell insulin secretion via complementary cAMP pathways and enhanced brown adipose tissue thermogenesis." },
              { icon: Flame, title: "GCGR Agonism", sub: "Energy Expenditure", color: "from-amber-500 to-orange-600", desc: "Increased hepatic fatty acid oxidation, resting energy expenditure +15-20%, and direct MASLD therapeutic potential." },
            ].map((item) => (
              <div key={item.title} className="bento-card p-7">
                <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-5 shadow-sm`}>
                  <item.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-xs font-semibold text-slate-400 tracking-wide uppercase mb-3">{item.sub}</p>
                <p className="text-sm leading-relaxed text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why GLP3-R Comparison Table */}
      <Suspense fallback={null}>
        <WhyGLP3R />
      </Suspense>

      {/* Key Takeaways */}
      <section data-testid="key-takeaways" className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bento-card p-8 md:p-10" style={{ background: "linear-gradient(135deg, #f0f9ff, #ffffff)" }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-7 h-7 rounded-lg bg-sky-100 flex items-center justify-center">
                <Target className="h-3.5 w-3.5 text-sky-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">Key Takeaways</h2>
            </div>
            <ul className="space-y-4">
              {[
                "Retatrutide (LY3437943) is the first triple-hormone receptor agonist targeting GLP-1, GIP, and glucagon receptors simultaneously.",
                "TRIUMPH-4 Phase III (Dec 2025): 28.7% mean body weight reduction at 68 weeks (12 mg dose) in adults with obesity and knee osteoarthritis — the largest weight loss from any anti-obesity agent in Phase III.",
                "TRANSCEND-T2D-1 Phase III (March 2026): 2.0% HbA1c reduction and 16.8% weight loss at 40 weeks in type 2 diabetes, with no plateau observed at study end.",
                "The glucagon receptor component increases energy expenditure by 15-20% and drives hepatic lipid clearance — Phase II showed 42.9% liver fat reduction (MASLD benefit).",
                "FDA NDA submission estimated Q4 2026–Q1 2027. Seven additional Phase III TRIUMPH readouts expected throughout 2026 across obesity, sleep apnea, MASLD, and cardiovascular outcomes."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3.5">
                  <span className="flex-shrink-0 w-6 h-6 rounded-lg text-white flex items-center justify-center text-xs font-bold mt-0.5" style={{ background: "#113C6E" }}>
                    {i + 1}
                  </span>
                  <span className="text-sm leading-relaxed text-slate-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Master Guide Body */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bento-card p-8 md:p-12 lg:p-16">
            <div className="max-w-3xl mx-auto article-prose">

              <p className="drop-cap text-base lg:text-lg leading-relaxed text-slate-600">
                Retatrutide represents a paradigm shift in the pharmacological approach to metabolic disease.
                Developed by Eli Lilly and Company under the investigational designation LY3437943, this
                novel peptide is the first molecule to simultaneously engage three distinct incretin and
                metabolic hormone receptors: the glucagon-like peptide-1 receptor (GLP-1R), the
                glucose-dependent insulinotropic polypeptide receptor (GIPR), and the glucagon receptor
                (GCGR). This triple-agonist mechanism positions Retatrutide at the frontier of metabolic
                research. For researchers seeking the highest-purity source,{" "}
                <a href="https://bioinfinity.co/retatrutide/glp3-r" target="_blank" rel="noopener noreferrer" className="font-bold underline decoration-2 underline-offset-2 transition-colors" style={{ color: "#113C6E" }} data-testid="intro-bioinfinity-link">
                  BioInfinity Lab's GLP3-R
                </a>{" "}
                is the gold standard — independently verified &ge;99% purity with full COA documentation.
              </p>

              <p className="text-base lg:text-lg leading-relaxed text-slate-600">
                The emergence of incretin-based therapies has transformed obesity and type 2 diabetes
                treatment. GLP-1 receptor agonists such as semaglutide demonstrated pharmacological
                weight loss previously reserved for bariatric surgery. Dual GLP-1/GIP agonists like
                tirzepatide further expanded the ceiling. Retatrutide extends this trajectory by
                incorporating glucagon receptor agonism — a mechanism that synergizes powerfully
                with GLP-1 and GIP signaling when combined in a single molecule.
              </p>

              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                1. The Triple-Agonist Mechanism of Action
              </h2>

              <h3 className="text-xl font-bold text-slate-900">
                1.1 GLP-1 Receptor Agonism
              </h3>

              <p className="text-base lg:text-lg leading-relaxed text-slate-600">
                The GLP-1 receptor component drives foundational metabolic effects. Upon activation
                in the hypothalamus and brainstem, Retatrutide reduces appetite through centrally
                mediated satiety signaling — modulating POMC and AgRP neurons in the arcuate nucleus.
                In the pancreas, GLP-1R agonism stimulates glucose-dependent insulin secretion while
                suppressing glucagon release. Additionally, GLP-1R activation slows gastric emptying,
                prolonging nutrient absorption and sustaining satiety.
              </p>

              <h3 className="text-xl font-bold text-slate-900">
                1.2 GIP Receptor Agonism
              </h3>

              <p className="text-base lg:text-lg leading-relaxed text-slate-600">
                GIP receptor agonism at supraphysiological doses bypasses the GIP resistance
                observed in type 2 diabetes and produces synergistic effects with GLP-1 signaling.
                GIP enhances beta cell insulin secretion through complementary cAMP pathways and
                promotes lipid buffering and thermogenesis in brown adipose tissue. The combined
                GLP-1/GIP agonism produces an amplified incretin effect exceeding either pathway alone.
              </p>

              <div className="pull-quote">
                "The addition of glucagon receptor agonism transforms what was purely an
                appetite-suppression strategy into a comprehensive metabolic intervention
                that simultaneously increases energy expenditure."
              </div>

              <h3 className="text-xl font-bold text-slate-900">
                1.3 Glucagon Receptor Agonism: The Differentiator
              </h3>

              <p className="text-base lg:text-lg leading-relaxed text-slate-600">
                The GCGR component fundamentally distinguishes Retatrutide from all existing
                incretin therapies. Glucagon stimulates hepatic glycogenolysis, gluconeogenesis,
                fatty acid oxidation, and elevates resting energy expenditure. When combined with
                GLP-1/GIP agonism, the hyperglycemic risk is neutralized, allowing increased
                energy expenditure and hepatic lipid clearance without glycemic destabilization.
                In preclinical models, the GCGR component increased resting energy expenditure
                by 15-20% — creating a dual-sided caloric deficit.
              </p>

              <div className="my-12">
                <DiscountCTA />
              </div>

              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                2. Clinical Evidence: Phase II and Phase III Results
              </h2>

              <p className="text-base lg:text-lg leading-relaxed text-slate-600">
                The pivotal Phase II study (NEJM, 2023) enrolled 338 adults with obesity.
                Participants receiving the 12 mg dose achieved 24.2% mean body weight reduction
                at 48 weeks — exceeding semaglutide (~15-17%) and tirzepatide (~22.5%). The
                8 mg group achieved 22.8%, and 4 mg achieved 17.9%. Weight loss trajectories
                had not plateaued at 48 weeks in higher-dose groups.
              </p>

              <h3 className="text-xl font-bold text-slate-900">
                2.1 TRIUMPH-4 Phase III Results (December 2025)
              </h3>

              <p className="text-base lg:text-lg leading-relaxed text-slate-600">
                The first successful Phase III readout — TRIUMPH-4 — confirmed and surpassed
                the Phase II signal. In adults with obesity or overweight and knee osteoarthritis,
                the 12 mg dose achieved a mean 28.7% body weight reduction (32.3 kg / 71.2 lbs
                from a baseline of 248.5 lbs) at 68 weeks, compared with 2.1% for placebo. The
                9 mg dose achieved 26.4% weight loss. Beyond weight, the trial demonstrated
                significant improvements in osteoarthritis pain (WOMAC pain score reduction of
                4.4-4.5 points vs. 2.4 for placebo), physical function, and cardiovascular risk
                markers including lowered non-HDL cholesterol, triglycerides, hsCRP, and systolic
                blood pressure by 14.0 mmHg at the 12 mg dose. Post-hoc analysis revealed that
                12.0-14.1% of participants on the higher doses were completely free of knee pain.
              </p>

              <h3 className="text-xl font-bold text-slate-900">
                2.2 TRANSCEND-T2D-1 Phase III Results (March 2026)
              </h3>

              <p className="text-base lg:text-lg leading-relaxed text-slate-600">
                The first Phase III trial in type 2 diabetes, TRANSCEND-T2D-1, reported topline
                results in March 2026, meeting its primary endpoint of HbA1c reduction from
                a 7.9% baseline. At 40 weeks: the 9 mg dose achieved 2.0% HbA1c reduction, the
                12 mg dose achieved 1.9%, and the 4 mg dose achieved 1.7% — all substantially
                greater than the 0.8% reduction with placebo. The 12 mg group also demonstrated
                16.8% weight loss at 40 weeks with no plateau observed, along with improvements
                in non-HDL cholesterol, triglycerides, and systolic blood pressure. Nausea was
                dose-dependent (16.4-26.5% across doses vs. placebo). Full results are expected
                at the American Diabetes Association Scientific Sessions in June 2026.
              </p>

              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                3. Safety Profile
              </h2>

              <p className="text-base lg:text-lg leading-relaxed text-slate-600">
                Across Phase II and Phase III, the adverse event profile remains consistent with
                the GLP-1 class. Gastrointestinal events — nausea, diarrhea, vomiting,
                constipation — were predominantly mild-moderate during dose escalation. In
                TRIUMPH-4, discontinuation rates were 12.2% (9 mg) and 18.2% (12 mg) vs. 4%
                placebo, though rates were lower (8.8-12.1%) in participants with BMI ≥35.
                In TRANSCEND-T2D-1, nausea ranged from 16.4-26.5% in a dose-dependent manner.
                No pancreatitis, MTC, or significant hypoglycemia has been reported across any
                Phase III study. Phase II MRI-PDFF data showed up to 42.9% reduction in liver
                fat content (12 mg), with 81-86% reduction at higher doses and 52-69% achieving
                normalization (&lt;5% by MRI) — confirming direct MASLD therapeutic relevance.
              </p>

              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                4. Pharmacokinetics and Dosing
              </h2>

              <p className="text-base lg:text-lg leading-relaxed text-slate-600">
                Retatrutide is administered once-weekly subcutaneously with a ~6-day half-life.
                The fatty diacid moiety enables albumin binding for extended residence time.
                Steady-state is achieved within 4-5 weeks. Dose escalation over 4-20 weeks
                mitigates GI tolerability concerns, validated by low discontinuation rates.
              </p>

              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                5. Regulatory Outlook and Future Directions
              </h2>

              <p className="text-base lg:text-lg leading-relaxed text-slate-600">
                With TRIUMPH-4 and TRANSCEND-T2D-1 now reporting positive results, Retatrutide's
                path to market is accelerating. Eli Lilly estimates FDA NDA submission in Q4
                2026 to Q1 2027, with UK MHRA approval potentially 6-12 months later and
                commercial availability projected around 2028. Seven additional Phase III
                readouts are expected throughout 2026: TRIUMPH-1 (obesity, ~2,300 participants,
                80-week data), TRIUMPH-2 (type 2 diabetes at 4/9/12 mg), TRIUMPH-3 (cardiovascular
                and renal outcomes), TRIUMPH-6 (weight loss maintenance over 80 weeks with
                36-week re-randomization), and trials in MASLD, chronic low back pain, and
                obstructive sleep apnea. Citi analysts project potential for &gt;30% weight loss
                in the 80-week TRIUMPH-1 data. Over 5,800 participants are enrolled across the
                combined TRIUMPH program. For researchers preparing protocols, sourcing matters — BioInfinity Lab's{" "}
                <a href="https://bioinfinity.co/retatrutide/glp3-r" target="_blank" rel="noopener noreferrer" className="font-bold underline decoration-2 underline-offset-2 transition-colors" style={{ color: "#113C6E" }} data-testid="closing-coa-link">
                  GLP3-R
                </a>{" "}
                remains the gold standard for batch consistency and transparent third-party testing.
              </p>

              <div className="pull-quote">
                "With TRIUMPH-4 delivering 28.7% weight loss at 68 weeks and TRANSCEND-T2D-1
                showing 2.0% HbA1c reduction — both exceeding expectations — Retatrutide is
                positioned to become the most potent metabolic pharmacotherapy ever approved."
              </div>

              <div className="my-12">
                <DiscountCTA />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Purity & Testing */}
      <Suspense fallback={null}>
        <PuritySection />
      </Suspense>

      {/* Bottom CTA */}
      <section data-testid="bottom-cta" className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bento-card p-10 md:p-14 text-center" style={{ background: "linear-gradient(160deg, #ffffff, #f8fafc)" }}>
            <div className="stagger-children max-w-xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mb-4">
                Explore the Research Data
              </h2>
              <p className="text-base leading-relaxed text-slate-500 mb-10">
                Dive into clinical trial results or calculate dosing protocols with our tools.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link
                  to="/clinical-data"
                  data-testid="bottom-cta-clinical"
                  className="btn-lift inline-flex items-center gap-2 px-7 py-3.5 text-white text-sm font-bold rounded-2xl transition-colors"
                  style={{ background: "#113C6E" }}
                >
                  <BookOpen className="h-4 w-4" />
                  Clinical Trial Data
                </Link>
                <Link
                  to="/dosage-calc"
                  data-testid="bottom-cta-dosage"
                  className="btn-lift inline-flex items-center gap-2 px-7 py-3.5 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-2xl shadow-card hover:border-slate-300 transition-all"
                >
                  <FlaskConical className="h-4 w-4" />
                  Dosage Calculator
                </Link>
                <a
                  href="#master-guide"
                  data-testid="bottom-cta-top"
                  className="btn-lift inline-flex items-center gap-2 px-7 py-3.5 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-2xl shadow-card hover:border-slate-300 transition-all"
                >
                  <Activity className="h-4 w-4" />
                  Back to Top
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
